import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Message.css';

const Message = ({ message, isUser, isHumanAgent = false, onClickableTextClick, t = {} }) => {
  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if this is a file/image message
  const isFileMessage = message.fileType === 'file' || message.fileType === 'image';
  const isImageMessage = message.fileType === 'image';

  // Function to parse and replace <click> tags with clickable span elements
  const parseClickableTags = (text) => {
    // Case-insensitive regex to match <click>content</click>
    const clickRegex = /<click>(.*?)<\/click>/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = clickRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add the clickable part
      parts.push({
        type: 'clickable',
        content: match[1],
        id: `click-${Date.now()}-${Math.random()}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const handleClickableClick = (text) => {
    if (onClickableTextClick && typeof onClickableTextClick === 'function') {
      onClickableTextClick(text);
    }
  };

  // Determine message class based on sender type
  const getMessageClass = () => {
    if (isUser) return 'message-user';
    if (isHumanAgent) return 'message-human-agent';
    return 'message-bot';
  };

  return (
    <div className={`message ${getMessageClass()}`}>
      <div className="message-wrapper">
        <div className="message-header">
          {!isUser && (
            <div className={`message-avatar ${isHumanAgent ? 'message-avatar-human' : ''}`}>
              {isHumanAgent ? (
                <span className="human-agent-avatar">👤</span>
              ) : (
                <img src="/assets/agent-logo.png" alt="Agent" className="agent-avatar-icon" />
              )}
            </div>
          )}
          <div className="message-time">
            {new Date(message.timestamp).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
        <div className="message-content">
          {/* Render file/image message */}
          {isFileMessage ? (
            <div className={`message-file ${isImageMessage ? 'message-file-image' : 'message-file-doc'}`}>
              {isImageMessage ? (
                // Image preview
                <div className="file-image-preview">
                  <img 
                    src={message.fileUrl} 
                    alt={message.fileName}
                    className="file-image"
                    onClick={() => window.open(message.fileUrl, '_blank')}
                  />
                </div>
              ) : (
                // Document file
                <a 
                  href={message.fileUrl} 
                  download={message.fileName}
                  className="file-doc-preview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="file-doc-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div className="file-doc-info">
                    <span className="file-doc-name">{message.fileName}</span>
                    <span className="file-doc-size">{formatFileSize(message.fileSize)}</span>
                  </div>
                  {/* Download icon for received files */}
                  {message.uploadStatus === 'received' && (
                    <div className="file-download-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </div>
                  )}
                </a>
              )}
              
              {/* Upload status indicator */}
              {message.uploadStatus === 'uploading' && (
                <div className="file-upload-status uploading">
                  <div 
                    className="file-upload-progress" 
                    style={{ width: `${(message.uploadProgress || 0) * 100}%` }}
                  />
                  <span className="file-upload-text">{t.uploading || 'جاري الإرسال...'}</span>
                </div>
              )}
              {message.uploadStatus === 'failed' && (
                <div className="file-upload-status failed">
                  <span className="file-upload-text">{t.uploadFailed || 'فشل الإرسال'}</span>
                </div>
              )}
              {message.uploadStatus === 'sent' && (
                <div className="file-upload-status sent">
                  <span className="file-upload-text">{t.uploadSent || '✓ تم الإرسال'}</span>
                </div>
              )}
              {message.uploadStatus === 'received' && (
                <div className="file-upload-status received">
                  <span className="file-upload-text">{t.fileReceived || '📥 ملف مستلم'}</span>
                </div>
              )}
            </div>
          ) : (
            // Regular text message
            <div className="message-text">
              {isUser ? (
                message.text
              ) : (
                <>
                  {parseClickableTags(message.text).map((part, index) => {
                    if (part.type === 'clickable') {
                      return (
                        <span
                          key={part.id || index}
                          className="clickable-text"
                          onClick={() => handleClickableClick(part.content)}
                          role="button"
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleClickableClick(part.content);
                            }
                          }}
                        >
                          {part.content}
                        </span>
                      );
                    } else {
                      return (
                        <ReactMarkdown 
                          key={index}
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Custom rendering for better Arabic RTL support
                            p: ({node, ...props}) => <p dir="auto" {...props} />,
                            ol: ({node, ...props}) => <ol dir="auto" {...props} />,
                            ul: ({node, ...props}) => <ul dir="auto" {...props} />,
                            li: ({node, ...props}) => <li dir="auto" {...props} />,
                            td: ({node, ...props}) => <td dir="auto" {...props} />,
                            th: ({node, ...props}) => <th dir="auto" {...props} />,
                            // Open links in a new window
                            a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />,
                          }}
                        >
                          {part.content}
                        </ReactMarkdown>
                      );
                    }
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;

